import { useEffect, useMemo, useState } from 'react'
import { Eye, Loader2 } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { useGetRolesQuery } from '@/redux/service/userManagement'
import {
  useGetRoleWidgetsQuery,
  useSetRoleWidgetsMutation,
  type WidgetSummary,
} from '@/redux/service/widgets'
import { WidgetPreviewDialog } from '@/components/widgets/WidgetPreviewDialog'
import { cn } from '@/lib/utils'
import toast from 'react-hot-toast'

export function RoleWidgetAssignment() {
  const { data: rolesData } = useGetRolesQuery({})
  const roles = rolesData?.data ?? []

  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null)
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set())
  const [previewWidget, setPreviewWidget] = useState<WidgetSummary | null>(null)

  const { data: roleWidgetsData, isFetching } = useGetRoleWidgetsQuery(
    selectedRoleId ?? 0,
    { skip: selectedRoleId === null }
  )
  const [setRoleWidgets, { isLoading: isSaving }] = useSetRoleWidgetsMutation()

  const savedIds = useMemo(() => {
    const ids = new Set<number>()
    if (!roleWidgetsData) return ids
    for (const group of roleWidgetsData.data) {
      for (const widget of group.widgets) {
        if (widget.assigned) ids.add(widget.id)
      }
    }
    return ids
  }, [roleWidgetsData])

  useEffect(() => {
    setCheckedIds(savedIds)
  }, [savedIds])

  const isDirty =
    checkedIds.size !== savedIds.size ||
    Array.from(checkedIds).some(id => !savedIds.has(id))

  const toggleWidget = (widgetId: number, checked: boolean) => {
    setCheckedIds(prev => {
      const next = new Set(prev)
      if (checked) next.add(widgetId)
      else next.delete(widgetId)
      return next
    })
  }

  const handleSave = async () => {
    if (selectedRoleId === null) return
    try {
      await setRoleWidgets({
        roleId: selectedRoleId,
        widget_ids: Array.from(checkedIds),
      }).unwrap()
      toast.success('Role widget access updated')
    } catch {
      toast.error('Failed to update role widget access')
    }
  }

  const totalWidgetCount =
    roleWidgetsData?.data.reduce(
      (sum, group) => sum + group.widgets.length,
      0
    ) ?? 0

  return (
    <div className='space-y-8'>
      <div className='flex flex-wrap items-center gap-4 rounded-xl border border-border bg-card/50 p-4'>
        <Select
          value={selectedRoleId !== null ? String(selectedRoleId) : ''}
          onValueChange={value => setSelectedRoleId(Number(value))}
        >
          <SelectTrigger className='w-64 h-9 text-xs bg-card'>
            <SelectValue placeholder='Select a role' />
          </SelectTrigger>
          <SelectContent>
            {roles.map(role => (
              <SelectItem key={role.id} value={String(role.id)}>
                {role.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selectedRoleId !== null && roleWidgetsData && (
          <span className='text-xs text-muted-foreground tabular-nums'>
            <span className='font-semibold text-foreground'>
              {checkedIds.size}
            </span>{' '}
            of {totalWidgetCount} widgets enabled
          </span>
        )}

        {selectedRoleId !== null && (
          <div className='ml-auto flex items-center gap-3'>
            {isDirty && (
              <span className='flex items-center gap-1.5 text-xs text-primary'>
                <span className='h-1.5 w-1.5 rounded-full bg-primary' />
                Unsaved changes
              </span>
            )}
            <Button
              onClick={handleSave}
              disabled={isSaving || !isDirty}
              className='h-9 px-4 text-xs'
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        )}
      </div>

      {selectedRoleId === null && (
        <p className='text-sm text-muted-foreground'>
          Select a role to manage its widget access.
        </p>
      )}

      {isFetching && (
        <div className='flex items-center gap-2 text-sm text-muted-foreground'>
          <Loader2 className='h-4 w-4 animate-spin' /> Loading widgets...
        </div>
      )}

      {roleWidgetsData?.data.map(group => {
        const enabledInGroup = group.widgets.filter(w =>
          checkedIds.has(w.id)
        ).length
        return (
          <div key={group.group_id} className='space-y-3'>
            <div className='flex items-center gap-3 border-b border-border pb-2.5'>
              <h3 className='text-xs font-bold uppercase tracking-widest text-foreground'>
                {group.group_name}
              </h3>
              <span className='text-xs text-muted-foreground'>
                ({group.widgets.length})
              </span>
              <span className='ml-auto text-xs text-muted-foreground tabular-nums'>
                {enabledInGroup}/{group.widgets.length} enabled
              </span>
            </div>
            <div className='rounded-lg border border-border bg-card divide-y divide-border overflow-hidden'>
              {group.widgets.map(widget => {
                const enabled = checkedIds.has(widget.id)
                return (
                  <div
                    key={widget.id}
                    className='flex items-center gap-4 px-4 py-3.5 transition-colors hover:bg-muted/40'
                  >
                    <span
                      className={cn(
                        'h-2 w-2 shrink-0 rounded-full',
                        enabled ? 'bg-emerald-500' : 'bg-muted-foreground/30'
                      )}
                    />
                    <div className='min-w-0 flex-1'>
                      <p className='truncate text-sm font-medium text-foreground'>
                        {widget.name}
                      </p>
                      <p className='truncate text-xs text-muted-foreground'>
                        {widget.description}
                      </p>
                    </div>
                    <Button
                      variant='ghost'
                      size='icon'
                      className='h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground'
                      onClick={() => setPreviewWidget(widget)}
                    >
                      <Eye className='h-4 w-4' />
                    </Button>
                    <Switch
                      checked={enabled}
                      onCheckedChange={checked =>
                        toggleWidget(widget.id, checked)
                      }
                    />
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}

      <WidgetPreviewDialog
        widget={previewWidget}
        onClose={() => setPreviewWidget(null)}
      />
    </div>
  )
}
